import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import {
  getPassengerLabel,
  isEmptyArray,
  isEmptyObject,
  isStringEmpty,
  parseEmptyArray,
  stringEmptyDefault,
} from '@common/helpers';
import { PassengerInformationInputModalEditArea } from '@common/helpers/constants/my-booking/passenger-information-input-modal.constant';
import { PassengerType } from '@common/interfaces';
import { FrequentFlyerCards } from '@common/interfaces/frequent-flyer-cards';
import { AirportI18n } from '@common/interfaces/master/airport_i18n_all';
import { CountryI18nBy2LetterCodes } from '@common/interfaces/master/country_i18n_by2lettercode';
import { MMileageProgramI18n } from '@common/interfaces/master/m_mileage_program_i18n';
import {
  DeduplicatedSecondContact,
  IDeduplicatedPhones,
  PassengerContactEmail,
  PassengerEmail,
  ServiceStatusTraveler,
} from '@common/interfaces/my-booking-passenger/my-booking-passenger.interface';
import { TransitInfoPassenger } from '@common/interfaces/mybooking/mybooking.interface';
import { CountryCountryI18nAllData } from '@common/master';
import { ServicingCommonStoreService } from '@common/services';
import { MyBookingPassengerService } from '@common/services/my-booking/my-booking-passenger/my-booking-passenger.service';
import { MyBookingStoreService } from '@common/services/my-booking/my-booking-store.service';
import { SupportComponent } from '@lib/components/support-class';
import { DateFormatPipe, StaticMsgPipe } from '@lib/pipes';
import { CommonLibService } from '@lib/services';
import { Subscription } from 'rxjs';
import {
  FrequentFlyerCard,
  GetOrderResponseData,
  GetOrderResponseDataAir,
  GetOrderResponseDataContacts,
  Traveler,
  TravelerRegulatoryDetailsVisasInner,
  Type1,
} from 'src/sdk-servicing';

@Component({
  selector: 'asw-passenger-information',
  templateUrl: './passenger-information.component.html',
  styleUrls: ['./passenger-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PassengerInformationComponent extends SupportComponent {
  travelers: Traveler[] = [];

  air: GetOrderResponseDataAir = {};

  contacts: GetOrderResponseDataContacts = {};

  pnr!: GetOrderResponseData;

  flights: Type1[] = [];

  transitInfo: TransitInfoPassenger = {};

  // [11.4] 滞在資格情報(US)
  lstVisaOrResidentAlienCardRegistrationRequired: string[] = [];

  // 国
  _mCountry: CountryI18nBy2LetterCodes = {};
  // 国(国際化)
  _mCountryI18N: Array<CountryCountryI18nAllData> = [];

  _mMileageProgramI18N: Array<MMileageProgramI18n> = [];
  _airportI18nAll: AirportI18n = {};

  private _subscriptions: Subscription = new Subscription();

  constructor(
    private _common: CommonLibService,
    private _myBookingPassengerService: MyBookingPassengerService,
    private _staticMsg: StaticMsgPipe,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private _change: ChangeDetectorRef,
    private readonly _dateFormat: DateFormatPipe,
    private readonly _myBookingStoreService: MyBookingStoreService
  ) {
    super(_common);
  }

  reload(): void {}
  init(): void {
    this._subscriptions.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        this.pnr = (servicingCommon?.getOrder as GetOrderResponseData) ?? ({} as GetOrderResponseData);
        this.air = this.pnr?.air ?? {};
        this.travelers = parseEmptyArray(this.pnr?.travelers as Traveler[]);
        this.contacts = this.pnr?.contacts ?? {};
        this.flights = parseEmptyArray(
          this.air?.bounds?.flatMap((bound) => {
            return parseEmptyArray<Type1>(bound?.flights);
          })
        );
        this.lstVisaOrResidentAlienCardRegistrationRequired =
          this._myBookingPassengerService.getListVisaOrResidentAlienCardRegistrationRequired(
            this.flights,
            this.pnr?.tasks
          );
        this._change.markForCheck();
      })
    );
    this._subscriptions.add(
      this._myBookingPassengerService.masterDataPassengerAction$.subscribe((masterData) => {
        if (!isEmptyObject(masterData)) {
          this._mCountry = masterData?.mCountry ?? {};
          this._mCountryI18N = masterData?.mCountryI18N as CountryCountryI18nAllData[];
          this._mMileageProgramI18N = masterData?.mMileageProgramI18N as MMileageProgramI18n[];
          this._airportI18nAll = masterData?.airportI18nAll ?? {};
          this._change.markForCheck();
        }
      })
    );
    this._subscriptions.add(
      this._myBookingStoreService.getMyBookingModel$().subscribe((state) => {
        this.transitInfo = state.transitInfo ?? {};
        this._change.markForCheck();
      })
    );
  }

  /** [8.4] 搭乗者メールアドレス登録状況 */
  isMoreThanOneEmail(traveler: Traveler): boolean {
    const passengerEmails = this.getEmailAddressesSK(traveler);
    /** 1. 当該搭乗者メールアドレスリスト[]が2件以上存在する場合、非表示とする。*/
    return passengerEmails.length >= 2;
  }

  /** [8.5] 搭乗者メールアドレス */
  isShowEmailAddress(traveler: Traveler): boolean {
    /** 搭乗者メールアドレス登録状況が個別指定、または当該搭乗者メールアドレスリストが存在する場合、 当該搭乗者メールアドレス[]を繰り返し、件数分メールアドレスを表示する。*/
    return (
      this.getPassengerEmailAddressRegistrationStatus(traveler) === 'label.specifyPerPassenger' ||
      this.isMoreThanOneEmail(traveler)
    );
  }

  /** [8.5] 搭乗者メールアドレス */
  getPassengerEmails(traveler: Traveler): PassengerEmail[] {
    return this.getEmailAddressesSK(traveler);
  }

  /**(4) 「搭乗者情報編集」ボタン押下時 */
  clickEditPassengerInformation(traveler: Traveler) {
    this._myBookingPassengerService.handleEventsWithPayload(
      PassengerInformationInputModalEditArea.TRAVELER_INPUT,
      traveler?.id ?? '',
      { isEditDuringStayInfo: true }
    );
  }

  /**4. 搭乗者姓名 */
  // [4.3] 搭乗者姓名
  getTravelerName(traveler: Traveler): string {
    return traveler?.names?.[0].title + ' ' + traveler?.names?.[0].firstName + ' ' + traveler?.names?.[0].lastName;
  }

  // [4.2] 搭乗者情報編集
  isDisplayEditButton(): boolean {
    /** 1.PNR情報取得API応答.data.orderEligibilities.travelerInput.isEligibleToForward = trueの場合、表示する。 */
    return Boolean(this.pnr?.orderEligibilities?.travelerInput?.isEligibleToForward);
  }

  /**4.2 搭乗者種別 */
  // [4.4] 搭乗者種別
  getPassengerTypeCode(traveler: Traveler): string {
    const passengerLabel = getPassengerLabel(stringEmptyDefault(traveler?.passengerTypeCode));
    return passengerLabel ? this._staticMsg.transform(passengerLabel) : '';
  }

  // [4.5] 生年月日
  isShowDateOfBirth() {
    /** PNR情報取得API応答.data.air.tripType = 'international'の場合、 非表示とする。"	 */
    return this.air?.tripType !== GetOrderResponseDataAir.TripTypeEnum.International;
  }

  // [4.5] 生年月日
  getDateOfBirth(traveler: Traveler): string {
    if (this.air?.tripType === GetOrderResponseDataAir.TripTypeEnum.Domestic) {
      /**
       * 1.PNR情報取得API応答.data.air.tripType = 'domestic'、かつ dateOfBirthが存在する場合、dateOfBirthを表示する。
       */
      if (traveler?.dateOfBirth) {
        return this._dateFormat.transform(stringEmptyDefault(traveler?.dateOfBirth), 'default_apiDate');
      }
      // 2.PNR情報取得API応答.data.air.tripType = 'domestic'、かつ dateOfBirthが存在しない場合、 未登録である旨の文言を表示する。
      return this._staticMsg.transform('label.hyphen');
    }
    /** 3.PNR情報取得API応答.data.air.tripType = 'international'の場合、 非表示とする。 */
    return '';
  }

  getMileageProgramInfo(traveler: Traveler): FrequentFlyerCard {
    // [2.1] PNR情報.data.frequentFlyerCards.<当該搭乗者情報.id>を該当マイレージプログラム情報として保持する。
    const applicableMileagePrograme = (this.pnr?.frequentFlyerCards as FrequentFlyerCards)?.[traveler?.id!];
    // 2.2 以下いずれかに該当する場合、マイレージプログラム情報を表示する。
    if (
      // 2.2.1 PNR情報取得API応答結果.data.orderEligibilities.travelerInput.isFrequentFlyerCardEligible = true。
      this.pnr?.orderEligibilities?.travelerInput?.isFrequentFlyerCardEligible ||
      // 2.2.2 以下いずれかに該当する。
      (!this.pnr?.orderType?.isAwardBooking && !isEmptyArray(applicableMileagePrograme?.fqtv)) ||
      (this.pnr?.orderType?.isAwardBooking && !isEmptyArray(applicableMileagePrograme?.fqtr))
    ) {
      if (!this.pnr?.orderType?.isAwardBooking) {
        return applicableMileagePrograme?.fqtv?.[0] ?? ({} as FrequentFlyerCard);
      } else {
        return applicableMileagePrograme?.fqtr?.[0] ?? ({} as FrequentFlyerCard);
      }
    }
    return {};
  }

  // [5.1] マイレージプログラム情報
  isDisplayFrequentFlyer(traveler: Traveler): boolean {
    return traveler?.passengerTypeCode !== PassengerType.INF;
  }

  // [5.2] マイレージプログラム
  getProgramName(traveler: Traveler) {
    const mileageProgramName = this._myBookingPassengerService.getMileageProgramFromMileageProgramI18N(
      this.getMileageProgramInfo(traveler),
      this._mMileageProgramI18N
    );
    if (!isStringEmpty(mileageProgramName?.mileage_program_name)) {
      return mileageProgramName?.mileage_program_name;
    }
    return stringEmptyDefault(this.getMileageProgramInfo(traveler)?.companyCode);
  }

  // [5.3] FFP番号
  getProgramNumber(traveler: Traveler): string {
    return stringEmptyDefault(this.getMileageProgramInfo(traveler)?.cardNumber);
  }

  // [5.4] 未登録の旨
  getNotRegisteredText(traveler: Traveler) {
    return this.getProgramName(traveler) || this.getProgramNumber(traveler)
      ? ''
      : this._staticMsg.transform('label.hyphen');
  }

  getIsShowMileageProgramInfo(traveler: Traveler) {
    return isStringEmpty(this.getNotRegisteredText(traveler));
  }

  // [8.1] 搭乗者連絡先情報
  isDisplayPassengerContactInfor(traveler: Traveler): boolean {
    return Boolean(traveler?.passengerTypeCode !== PassengerType.INF);
  }

  // [8.2] 搭乗者連絡先情報（米国旅程を含む場合)
  get isUsContactRequired(): boolean {
    /** 1.PNR情報取得API応答.data.tasks.isUsContactRequired = trueの場合、表示する。*/
    return Boolean(this.pnr?.tasks?.isUsContactRequired);
  }

  // [8.4] 搭乗者メールアドレス登録状況
  getEmailAddressesSK(traveler: Traveler): PassengerEmail[] {
    /** PNR情報取得API応答.data.contacts.<当該搭乗者情報.id>.emails[]を当該搭乗者SKメールアドレスリストとして保持する。*/
    return (this.contacts?.[traveler?.id!]?.emails as PassengerEmail[]) ?? ([] as PassengerEmail[]);
  }

  // [8.4] 搭乗者メールアドレス登録状況
  getPassengerEmailAddressRegistrationStatus(traveler: Traveler): string {
    const passengerEmails = this.getEmailAddressesSK(traveler);
    if (!isEmptyArray(passengerEmails)) {
      /** 2. 上記以外、かつ当該搭乗者SKメールアドレス[0].isSameAsRepresentative=trueの場合、代表者メールアドレスと同じである旨。*/
      if (Boolean(passengerEmails[0]?.isSameAsRepresentative) === true) {
        return 'label.sameAsRepresentativeMailAddress';
      } else {
        /** 3. 上記以外、かつ当該搭乗者SKメールアドレスリストが存在する場合、個別指定である旨を表示する。*/
        return 'label.specifyPerPassenger';
      }
    } else {
      /** 4. 当該搭乗者SKメールアドレスリストが存在しない場合、未登録である旨。*/
      return 'label.hyphen';
    }
  }

  /** [8.16] その他の搭乗者メール送付先の見出し */
  getPassengerSSRCTCEEmail(traveler: Traveler): PassengerContactEmail[] {
    /** 1.PNR情報取得API応答.data.contacts.<当該搭乗者情報.id>.contactEmails[].addressを一つのリストにして重複を除いた物を当該搭乗者SSR CTCEメールアドレスリストとして保持する。*/
    const contactEmails =
      (this.contacts?.[traveler?.id!]?.contactEmails as PassengerContactEmail[]) ?? ([] as PassengerContactEmail[]);
    const uniqueContactEmail = new Map<string, PassengerContactEmail>();
    contactEmails.forEach((email) => {
      if (!uniqueContactEmail.has(String(email?.address))) {
        uniqueContactEmail.set(String(email?.address), email);
      }
    });
    return [...uniqueContactEmail.values()];
  }

  /** [8.16] その他の搭乗者メール送付先の見出し */
  getPassengerSKEmail(traveler: Traveler): PassengerEmail[] {
    /** 2.PNR情報取得API応答.data.contacts.<当該搭乗者情報.id>.emails[].addressを一つのリストにして重複を除いた物を当該搭乗者SKメールアドレスリストとして保持する。*/
    const emails = (this.contacts?.[traveler?.id!]?.emails as PassengerEmail[]) ?? ([] as PassengerEmail[]);
    const uniqueEmail = new Map<string, PassengerEmail>();
    emails.forEach((email) => {
      if (!uniqueEmail.has(String(email?.address))) {
        uniqueEmail.set(String(email?.address), email);
      }
    });
    return [...uniqueEmail.values()];
  }

  /** [8.16] その他の搭乗者メール送付先の見出し */
  filterPassengerSSRCTCEEmailAndSKEmail(traveler: Traveler): PassengerContactEmail[] {
    /** 3.空の搭乗者メール送付先リストを作成する。*/
    let passengerEmailDestinationList: PassengerContactEmail[] = [];

    const passengerSSRCTCEEmails = this.getPassengerSSRCTCEEmail(traveler);
    const passengerSKEmails = this.getPassengerSKEmail(traveler);
    /** ＜以下、当該搭乗者SSR CTCEメールアドレスリストの要素数繰り返し＞（それぞれの要素が当該搭乗者SSR CTCEメールアドレス）*/
    passengerSSRCTCEEmails.forEach((email) => {
      /** 4.当該搭乗者SKメールアドレスリストに当該搭乗者SSR CTCEメールアドレスが含まれていない場合、当該搭乗者SSR CTCEメールアドレスを搭乗者メール送付先リストに追加する。*/
      // ※小文字と大文字を分別しない
      if (
        !passengerSKEmails.some(
          (skEmail) => (skEmail?.address ?? '').toLowerCase() === (email?.address ?? '').toLowerCase()
        )
      ) {
        passengerEmailDestinationList.push(email);
      }
    });
    /** <ここまで、当該搭乗者SSR CTCEメールアドレスリストの要素数繰り返し＞ */
    return passengerEmailDestinationList;
  }

  /** [8.16] その他の搭乗者メール送付先の見出し */
  isShowPassengerSSRCTCEEmailAndSKEmail(traveler: Traveler): boolean {
    /**
     * 5.搭乗者メール送付先リストが存在する場合、用途として入国管理にて利用される旨を含む文言を表示する。
     * 6.搭乗者メール送付先リストが存在しない場合、非表示とする。
     */
    if (!isEmptyArray(this.filterPassengerSSRCTCEEmailAndSKEmail(traveler))) {
      return true;
    }
    return false;
  }

  /** [8.17] その他の搭乗者メールアドレス */
  isShowMorePassengerSSRCTCEEmail(traveler: Traveler): boolean {
    /**
     * 1.その他の搭乗者メール送付先の見出しが表示されている場合、当該搭乗者メールアドレスリストを繰り返し、件数分メールアドレスを表示する。
     * ※複数件の場合、メールアドレス毎に改行して表示する。
     * ※上3桁以降がマスキングされた状態で返却される。
     * 2.上記以外の場合、非表示とする。
     */
    return !isEmptyArray(this.filterPassengerSSRCTCEEmailAndSKEmail(traveler));
  }

  /** [8.12] 搭乗者メールアドレス登録状況 */
  isMoreThanOnePassengerEmail(traveler: Traveler): boolean {
    const passengerEmails = this.getPassengerEmailAddress(traveler);
    /** 1. 当該搭乗者メールアドレスリスト[]が2件以上存在する場合、非表示とする。*/
    return passengerEmails.length >= 2;
  }

  // [8.12] 搭乗者メールアドレス登録状況
  passengerEmailAddress(traveler: Traveler): string {
    const passengerEmailAddress = this.getPassengerEmailAddress(traveler);
    if (!isEmptyArray(passengerEmailAddress)) {
      /** 2. 上記以外、かつPNR情報取得API応答.data.contacts.<当該搭乗者情報.id>.emails.isSameAsRepresentative=trueの場合、代表者メールアドレスと同じである旨。*/
      const emails = (this.contacts?.[traveler?.id!]?.emails as PassengerEmail[]) ?? ([] as PassengerEmail[]);
      const isSameAsRepresentative = emails.every((email) => Boolean(email?.isSameAsRepresentative) === true);
      if (isSameAsRepresentative) {
        return 'label.sameAsRepresentativeMailAddress';
      } else {
        /** 3. 上記以外、かつ当該搭乗者メールアドレスリスト[]が存在する場合、個別指定である旨を表示する。個別指定である旨 */
        return 'label.specifyPerPassenger';
      }
    } else {
      /** 4. 当該搭乗者メールアドレスリスト[]が存在しない場合、未登録である旨。*/
      return 'label.hyphen';
    }
  }

  /** [8.12] 搭乗者メールアドレス登録状況 */
  getPassengerEmailAddress(traveler: Traveler): string[] {
    let passengerEmails: string[] = [];
    // PNR情報取得API応答.data.contacts.<当該搭乗者情報.id>.emails[]を当該搭乗者SKメールアドレスリストとして保持する。
    const emails = (this.contacts?.[traveler?.id!]?.emails as PassengerEmail[]) ?? ([] as PassengerEmail[]);
    emails.forEach((email: PassengerEmail) => {
      passengerEmails.push(String(email?.address).toLowerCase());
    });
    return passengerEmails;
  }

  /** [8.13] 搭乗者メールアドレス */
  isShowPassengerEmailAddress(traveler: Traveler): boolean {
    /** 搭乗者メールアドレス登録状況が個別指定、または当該搭乗者メールアドレスリストが複数件存在する場合、 当該搭乗者メールアドレス[]を繰り返し、件数分メールアドレスを表示する。*/
    return (
      this.passengerEmailAddress(traveler) === 'label.specifyPerPassenger' || this.isMoreThanOnePassengerEmail(traveler)
    );
  }

  // [8.7][8.14] フライト情報SMS送付先登録状況
  getSMSInformationUS(traveler: Traveler): string {
    // 2. 上記以外の場合、以下の条件に応じて、該当旨を表示する。
    const smsInformations = this.contacts?.[traveler?.id!]?.deduplicatedPhones;

    // 2.1. 当該搭乗者SMS情報[]が空である場合、登録必須である旨。
    if (isEmptyArray(smsInformations)) {
      return 'label.requestForRegistration';
    }

    // 2.2. 当該搭乗者SMS情報[0].isSameAsRepresentative = trueの場合、代表者と同一である旨。
    if (smsInformations?.[0]?.isSameAsRepresentative === true) {
      return 'label.sameAsRepresentativePhone';
    }

    // 2.3. 上記以外、かつ当該搭乗者SMS情報[]が存在する場合 、個別指定である旨を表示する。
    return 'label.sendToPassengerPhoneNumber1';
  }

  /**[8.7] [8.14] フライト情報SMS送付先登録状況 */
  isShowSMSInformationUS(traveler: Traveler) {
    /**
     * Note: Do the reverse of the DD description to display SMS
     * PNR情報取得API応答.data.contacts.<当該搭乗者情報.id>.deduplicatedPhones[]を当該搭乗者SMS情報として保持する。
     * 1. 以下条件いずれかに該当する場合、表示しない。
     * 1.1 PNR情報取得API応答.data.air.isContainedNhGroupOperatingSegment = false、またはPNR情報取得API応答.data.air.isContainedNhGroupMarketingSegment = false。
     * 1.2 PNR情報取得API応答.data.orderType.isGroupBooking = true。
     * 1.3 当該搭乗者SMS情報[]が2件以上が存在する。
     */
    return (
      this.air?.isContainedNhGroupOperatingSegment &&
      this.air?.isContainedNhGroupMarketingSegment &&
      !this.pnr?.orderType?.isGroupBooking &&
      parseEmptyArray(this.contacts?.[traveler?.id!]?.deduplicatedPhones)?.length < 2
    );
  }

  // [8.8][8.15] フライト情報SMS送付先番号
  isDisplayFlightInformationSMSDestinationNumber(traveler: Traveler) {
    return (
      // フライト情報SMS送付先登録状況が個別指定である旨が表示されている場合
      this.getSMSInformationUS(traveler) === 'label.sendToPassengerPhoneNumber1' ||
      // 、または当該搭乗者SMS情報が複数件存在する場合、重複削除済の当該搭乗者SMS情報を繰り返し、件数分の電話番号を表示する。
      this.getPhoneInformationUS(traveler)?.length > 1
    );
  }

  // [8.8] [8.15] フライト情報SMS送付先番号
  getPhoneInformationUS(traveler: Traveler) {
    const phones = parseEmptyArray<IDeduplicatedPhones>(this.contacts?.[traveler?.id!]?.deduplicatedPhones);
    const uniquePhone = new Map<string, IDeduplicatedPhones>();
    phones.forEach((phone) => {
      if (!uniquePhone.has(phone.number)) {
        uniquePhone.set(phone.number, phone);
      }
    });
    return [...uniquePhone.values()];
  }

  // [8.9] 2件目緊急連絡先電話番号
  getPhoneSecondContact(traveler: Traveler) {
    const deduplicatedSecondContact = parseEmptyArray<DeduplicatedSecondContact>(
      this.contacts?.[traveler?.id!]?.deduplicatedSecondContact
    );

    return deduplicatedSecondContact;
  }

  // [8.9] 2件目緊急連絡先電話番号
  isShowPhoneSecondContract(traveler: Traveler) {
    /** PNR情報取得API応答.data.contacts.<当該搭乗者情報.id>.deduplicatedSecondContactを該当2件目緊急連絡先として保持する。*/
    return !isEmptyArray(this.contacts?.[traveler?.id!]?.deduplicatedSecondContact);
  }

  // [9.1] パスポート情報
  isDisplayPassportInfor() {
    /** 1.PNR情報取得API応答.data.air.tripType = 'international'の場合、表示する。 */
    return this.air?.tripType === GetOrderResponseDataAir.TripTypeEnum.International;
  }

  // [9.2] パスポート番号
  getPassportRegistrationStatus(traveler: Traveler) {
    /** 1.当該搭乗者情報.regulatoryDetails.isPassportRegistered = trueの場合、label.registeredを表示する。 */
    /** 2.それ以外の場合、label.hyphenを表示する。"	 */
    return traveler?.regulatoryDetails?.isPassportRegistered ? 'label.registered' : 'label.hyphen';
  }

  // [11.1] 入国関連情報
  isDisplayImmigrationInformation(traveler: Traveler): boolean {
    // 1.当該搭乗者情報.regulatoryDetailsを当該入国関連情報として保持する。
    const regulatoryDetails = traveler.regulatoryDetails;

    // 2.PNR情報取得API応答.data.air.tripType = 'international'の場合、
    if (this.air?.tripType === GetOrderResponseDataAir.TripTypeEnum.International) {
      // 2.1.以下のいずれかの条件を満たす場合に表示する。
      if (
        // 2.2.1.PNR情報取得API応答.data.tasks.<全セグメントID>.isVisaOrResidentAlienCardRegistrationRequired = true要素が存在する
        this.flights.some(
          (flight) =>
            this.pnr?.tasks?.[stringEmptyDefault(flight.id)]?.isVisaOrResidentAlienCardRegistrationRequired === true
        ) ||
        // 2.2.2.PNR情報取得API応答.data.tasks.isHomeAddressRequired = true
        this.pnr?.tasks?.isHomeAddressRequired === true ||
        // 2.2.3.当該入国関連情報.isRedressNumberRegistered = true
        regulatoryDetails?.isRedressNumberRegistered === true ||
        // 2.2.4.当該入国関連情報.isKnownTravellerNumberRegistered = true
        regulatoryDetails?.isKnownTravellerNumberRegistered === true ||
        // 2.2.5.当該入国関連情報.isChineseCitizenIdentificationNumberRegistered = true
        regulatoryDetails?.isChineseCitizenIdentificationNumberRegistered === true ||
        // 2.2.6.当該入国関連情報.visasの内に、registeredStatus=""completed""(登録済み)要素が存在する
        regulatoryDetails?.visas?.some(
          (visa) => visa.registeredStatus === TravelerRegulatoryDetailsVisasInner.RegisteredStatusEnum.Completed
        )
      ) {
        return true;
      }

      // 2.2.上記以外の場合、非表示とする。
      return false;
    }

    return false;
  }

  // [11.2] 未登録の旨
  getImmigrationInformationStatus(traveler: Traveler) {
    if (this.checkCase3ImmigrationInformation(traveler) || this.checkCase4ImmigrationInformation(traveler)) {
      return this._staticMsg.transform('label.requestForRegistration');
    } else {
      return this._staticMsg.transform('label.hyphen');
    }
  }

  // [11.2] 未登録の旨
  checkCase1ImmigrationInformation(traveler: Traveler): boolean {
    const { regulatoryDetails } = traveler;
    return (
      // 11.2.1 当該搭乗者情報.passengerTypeCode != ""INF""以外の場合、以下条件全てを満たす場合に表示する。
      traveler?.passengerTypeCode !== PassengerType.INF &&
      // 11.2.1.1 当該入国関連情報.isChineseCitizenIdentificationNumberRegistered = false
      !regulatoryDetails?.isChineseCitizenIdentificationNumberRegistered &&
      // 11.2.1.2 当該入国関連情報.visasが空である。
      isEmptyArray(regulatoryDetails?.visas) &&
      // 11.2.1.3 当該入国関連情報.isGreenCardRegistered = false
      !regulatoryDetails?.isGreenCardRegistered &&
      // 11.2.1.4 当該入国関連情報.isDestinationAddressRegistered = false (※トランジットの場合は有となる)
      !regulatoryDetails?.isDestinationAddressRegistered &&
      // 11.2.1.5 当該入国関連情報.ishomeAddressCountriesRegistered=false
      !regulatoryDetails?.ishomeAddressCountriesRegistered &&
      // 11.2.1.6 当該入国関連情報.isRedressNumberRegistered= false
      !regulatoryDetails?.isRedressNumberRegistered &&
      // 11.2.1.7 当該入国関連情報.isKnownTravellerNumberRegistered = false。
      !regulatoryDetails?.isKnownTravellerNumberRegistered
    );
  }

  // [11.2] 未登録の旨
  checkCase2ImmigrationInformation(traveler: Traveler): boolean {
    const { regulatoryDetails } = traveler;
    return (
      /** 2.当該搭乗者情報.passengerTypeCode = ""INF""の場合、以下条件全てを満たす場合に表示する。 */
      traveler?.passengerTypeCode === PassengerType.INF &&
      /** 2.1 当該入国関連情報.visasが空である。 */
      isEmptyArray(regulatoryDetails?.visas) &&
      /** 2.2 当該入国関連情報.isGreenCardRegistered = false。 */
      !regulatoryDetails?.isGreenCardRegistered &&
      /** 2.3 当該入国関連情報.isRedressNumberRegistered= false。 */
      !regulatoryDetails?.isRedressNumberRegistered &&
      /** 2.4 当該入国関連情報.isKnownTravellerNumberRegistered = false。 */
      !regulatoryDetails?.isKnownTravellerNumberRegistered
    );
  }

  // [11.2] 未登録の旨
  checkCase3ImmigrationInformation(traveler: Traveler): boolean {
    const flight = this.getFlightIsVisaOrResidentAlienCardRegistrationRequired();
    return (
      // 1. 当該搭乗者情報.passengerTypeCode = "INF"以外の場合、以下条件いずれかに該当する場合、登録を促す旨の文言、全てに該当しない場合、登録されていない旨。
      traveler?.passengerTypeCode !== PassengerType.INF &&
      // 1.1 PNR情報取得API応答.data.tasks.<セグメントID>[].isVisaOrResidentAlienCardRegistrationRequired = true要素が存在する。
      (this.pnr?.tasks?.isHomeAddressRequired === true ||
        // 1.2.PNR情報取得API応答.data.tasks.isHomeAddressRequired = true。
        !isEmptyObject(flight))
    );
  }

  // [11.2] 未登録の旨
  checkCase4ImmigrationInformation(traveler: Traveler): boolean {
    /**
     * 2. 当該搭乗者情報.passengerTypeCode = "INF"の場合、以下条件の全てに該当する場合、登録を促す旨の文言、全てに該当しない場合、登録されていない旨。
     * 2.1 PNR情報取得API応答.data.tasks.<セグメントID>[].isVisaOrResidentAlienCardRegistrationRequired = true要素が存在する。
     * 2.2 当該入国関連情報.isDestinationAddressRegistered = false
     */
    const flight = this.getFlightIsVisaOrResidentAlienCardRegistrationRequired();
    return (
      traveler?.passengerTypeCode === PassengerType.INF &&
      !isEmptyObject(flight) &&
      traveler?.regulatoryDetails?.isDestinationAddressRegistered === false
    );
  }

  // [11.4] 滞在資格情報(US)
  getStatusInformationUS(traveler: Traveler): string {
    const { regulatoryDetails } = traveler;

    /** 1.1 当該搭乗者情報.passengerTypeCode = ""INF""以外の場合、以下の条件に応じた内容を表示する。 */
    if (traveler?.passengerTypeCode !== PassengerType.INF) {
      /** 1.1 当該入国関連情報..isUsVisaRegistered = false、かつ当該入国関連情報.isGreenCardRegistered = falseの場合、以下の条件に応じた内容を表示する。 */
      if (!regulatoryDetails?.isUsVisaRegistered && !regulatoryDetails?.isGreenCardRegistered) {
        /** 1.1.1 当該入国関連情報.isDestinationAddressRegistered = trueの場合、以下の条件に応じた内容を表示する。 */
        if (regulatoryDetails?.isDestinationAddressRegistered) {
          /** 1.1.1.1 予約詳細store.トランジット情報<当該搭乗者情報.id>.isTransit=trueの場合、トランジットである旨。*/
          if (Boolean(this.transitInfo[String(traveler?.id)].isTransit) === true) {
            return this._staticMsg.transform('label.noStopover');
          } else {
            /** 1.1.1.2 上記以外の場合、滞在先情報が登録済みである旨。*/
            return this._staticMsg.transform('label.registered');
          }
        } else {
          /** 1.1.2 上記以外の場合、USの滞在先資格情報の登録を促す旨 */
          return this._staticMsg.transform('label.requestForRegistration');
        }
      }
      /** 1.2 当該入国関連情報..isUsVisaRegistered = true、かつ当該入国関連情報.isGreenCardRegistered = falseの場合、以下の条件に応じた内容を表示する。 */
      if (regulatoryDetails?.isUsVisaRegistered && !regulatoryDetails?.isGreenCardRegistered) {
        /** 1.2.1 当該入国関連情報.isDestinationAddressRegistered = trueの場合、visa(US)登録済みの旨 */
        if (regulatoryDetails?.isDestinationAddressRegistered) {
          return this._staticMsg.transform('label.visa');
        } else {
          /** 1.2.2 上記以外の場合、USの滞在先資格情報の登録を促す旨 */
          return this._staticMsg.transform('label.requestForRegistration');
        }
      }
      /** 1.3  当該入国関連情報..isUsVisaRegistered = false、かつ当該入国関連情報.isGreenCardRegistered = trueの場合、以下の条件に応じた内容を表示する。 */
      if (!regulatoryDetails?.isUsVisaRegistered && regulatoryDetails?.isGreenCardRegistered) {
        /** 1.3.1 当該入国関連情報.isDestinationAddressRegistered = trueの場合、グリーンカード登録済みの旨。*/
        if (regulatoryDetails.isDestinationAddressRegistered) {
          return this._staticMsg.transform('label.greenCard');
        } else {
          /** 1.3.2 上記以外の場合、USの滞在先資格情報の登録を促す旨 */
          return this._staticMsg.transform('label.requestForRegistration');
        }
      }
    }

    /**  当該搭乗者情報.passengerTypeCode = ""INF""の場合、、以下の条件に応じた内容を表示する。 */
    if (traveler?.passengerTypeCode === PassengerType.INF) {
      /**  当該入国関連情報..isUsVisaRegistered = true、かつ当該入国関連情報.isGreenCardRegistered = falseの場合、visa(US)登録済みの旨。*/
      if (regulatoryDetails?.isUsVisaRegistered && !regulatoryDetails?.isGreenCardRegistered) {
        return this._staticMsg.transform('label.visa');
      }
      /** 2.2 当該入国関連情報..isUsVisaRegistered = false、かつ当該入国関連情報.isGreenCardRegistered = trueの場合、グリーンカード登録済みの旨。*/
      if (!regulatoryDetails?.isUsVisaRegistered && regulatoryDetails?.isGreenCardRegistered) {
        return this._staticMsg.transform('label.greenCard');
      }
      /** 2.3 当該入国関連情報..isUsVisaRegistered = false、
       * かつ当該入国関連情報.isGreenCardRegistered = false、
       * 当該入国関連情報.isDestinationAddressRegistered=trueの場合
       * 登録されていない旨 */
      if (
        !regulatoryDetails?.isUsVisaRegistered &&
        !regulatoryDetails?.isGreenCardRegistered &&
        regulatoryDetails?.isDestinationAddressRegistered
      ) {
        return this._staticMsg.transform('label.hyphen');
      }
      /** 2.4.当該入国関連情報..isUsVisaRegistered=false、
       * かつ当該入国関連情報.isGreenCardRegistered=false、
       * かつ当該入国関連情報.isDestinationAddressRegistered=falseの場合、
       * 登録を促す旨。
       */
      if (
        !regulatoryDetails?.isUsVisaRegistered &&
        !regulatoryDetails?.isGreenCardRegistered &&
        !regulatoryDetails?.isDestinationAddressRegistered
      ) {
        return this._staticMsg.transform('label.requestForRegistration');
      }
    }

    return '';
  }

  // [11.4] 滞在資格情報(US)
  isDisplayStatusInformationUS() {
    return !isEmptyArray(this.lstVisaOrResidentAlienCardRegistrationRequired);
  }

  // [11.5] 滞在資格情報(US以外)
  getListVisaInformation(traveler: Traveler) {
    return this._myBookingPassengerService.getListVisaInformation(
      this.flights,
      traveler,
      this.pnr?.tasks,
      this._mCountry
    );
  }

  // [11.5] 滞在資格情報(US以外)
  isDisplayStayInformationOtherThanUS(traveler: Traveler) {
    // 4. visa情報リストが存在しない場合、非表示とする。
    // 5. visa情報リストが存在する場合、以下の内容を表示する。
    return !isEmptyArray(this.getListVisaInformation(traveler));
  }

  // part of [11.6] 国ごとの滞在資格情報(項目名)
  getCountryName(visaInfor: TravelerRegulatoryDetailsVisasInner) {
    let country3LetterCode = visaInfor?.issuanceCountryCode;
    if (country3LetterCode?.length === 2) {
      country3LetterCode = this._myBookingPassengerService.getCountry3LetterCodeFromMCountry(
        visaInfor?.issuanceCountryCode,
        this._mCountry
      );
    }

    const countryName = this._myBookingPassengerService.getCountryNameFromMCountryI18N(
      country3LetterCode,
      this._mCountryI18N
    );
    return countryName;
  }

  // [11.7] 国ごとの滞在資格情報
  getResidenceEligibilityByCountry(country: TravelerRegulatoryDetailsVisasInner): string {
    /** 1.当該visa情報.registeredStatus = 'partial'の場合、visa登録を促す旨、*/
    if (country?.registeredStatus === TravelerRegulatoryDetailsVisasInner.RegisteredStatusEnum.Partial) {
      return this._staticMsg.transform('label.requestForRegistration');
    }
    /** 2.当該visa情報.registeredStatus = 'completed'の場合、visa登録済みの旨*/
    if (country?.registeredStatus === TravelerRegulatoryDetailsVisasInner.RegisteredStatusEnum.Completed) {
      return this._staticMsg.transform('label.visa');
    }
    // 3. 上記以外の場合、非表示とする。
    return '';
  }

  // [11.8] 中国公民身分番号
  isDisplayChinaCivilIdentificationNumber(traveler: Traveler): boolean {
    /** 1. PNR情報取得API応答.data.tasks.isChineaseCitizenIdentificationNumberRegistrationRequired = true、かつ当該入国関連情報.isChineseCitizenIdentificationNumberRegistered = true 場合、label.registeredを表示する。 */
    const tasks = this.pnr?.tasks;
    return Boolean(
      traveler?.regulatoryDetails?.isChineseCitizenIdentificationNumberRegistered &&
        tasks?.isChineseCitizenIdentificationNumberRegistrationRequired
    );
  }

  // [11.9] 居住国
  isDisplayCountryOfResidence(traveler: Traveler) {
    /**
     * 1. PNR情報取得API応答.data.tasks.isHomeAddressRequired = true、かつ報.ishomeAddressCountriesRegistered=trueの場合、登録済みの旨。
     * 2. 上記以外の場合、非表示とする。
     * */
    const tasks = this.pnr?.tasks;
    return Boolean(traveler?.regulatoryDetails?.ishomeAddressCountriesRegistered && tasks?.isHomeAddressRequired);
  }

  // [11.10] Redress Number
  isDisplayRedressNumber(traveler: Traveler) {
    /**
     * 1. PNR情報取得API応答.data.tasks.isRedressNumberRegistrationRequired= true、かつ当該入国関連情報.isRedressNumberRegistered = trueの場合、登録済みの旨。
     * 2. 上記以外の場合、非表示とする。"
     */
    const tasks = this.pnr?.tasks;

    return Boolean(
      traveler?.regulatoryDetails?.isRedressNumberRegistered && tasks?.isRedressNumberRegistrationRequired
    );
  }

  // [11.11] Known Traveler Number
  isDisplayKnownTravelerNumber(traveler: Traveler) {
    const tasks = this.pnr?.tasks;

    return Boolean(
      traveler?.regulatoryDetails?.isKnownTravellerNumberRegistered && tasks?.isKnownTravelerNumberRegistrationRequired
    );
  }

  // [12.2] 発着通知連絡先
  isDisplayArrivalDepartureAlert(traveler: Traveler) {
    /**
     * 1. 以下のいずれかの条件を満たす場合、非表示とする。
     * 1.1. 当該搭乗者情報.passengerTypeCode = 'INF'
     * 1.2. PNR情報取得API応答.data.orderEligibilities.travelerInput.departureArrivalNotification.isEligible = false
     */
    return (
      traveler?.passengerTypeCode !== PassengerType.INF &&
      // FIXME wait for update SDK
      this.pnr?.orderEligibilities?.travelerInput?.departureArrivalNotification?.isEligible
    );
  }

  // [12.3] 発着通知連絡先情報
  getArrivalDepartureAlertStatus(traveler: Traveler) {
    /**
     * 1.PNR情報取得API応答.data.contacts.<当該搭乗者情報.id>.departureArrivalNotifications[]が1件以上存在する場合、登録済みである。
     * 2.それ以外の場合、label.hyphenを表示する。
     */
    return this.contacts?.[traveler?.id!]?.departureArrivalNotifications?.length > 0
      ? 'label.registered'
      : 'label.hyphen';
  }

  // [13.2] 搭乗者情報系サポート情報
  isDisplaySupportInformation(traveler: Traveler) {
    // 1. If the passenger information .passengerTypeCode = 'INF', it shall be hidden.
    // 2.それ以外で、PNR情報取得API応答.data.orderEligibilities.travelerInput.isPassengerSupportEligible = true、またはPNR情報取得API応答.data.travelerSupports.<当該搭乗者情報.id>.isSupportRegistered=trueの場合、搭乗者情報系サポートを基に表示する
    return (
      traveler?.passengerTypeCode !== PassengerType.INF &&
      (this.pnr?.orderEligibilities?.travelerInput?.isPassengerSupportEligible === true ||
        (this.pnr?.travelerSupports as any)?.[traveler?.id!]?.isSupportRegistered === true)
    );
  }

  // [13.2] サポート情報登録状況
  getSupportInformationStatus(traveler: Traveler) {
    let statusLabel = '';
    const supportByTravelerId = (this.pnr?.travelerSupports as any)?.[traveler?.id!];
    const serviceStatus = supportByTravelerId?.serviceStatus;

    if (serviceStatus === ServiceStatusTraveler.ServiceStatusTravelerEnum.Applied) {
      // 1. PNR情報取得API応答.data.travelerSupports.<当該搭乗者情報.id>.serviceStatus = ""applied""の場合、登録済みの旨。
      statusLabel = 'label.registered';
    } else if (serviceStatus === ServiceStatusTraveler.ServiceStatusTravelerEnum.Cancelled) {
      // 2. PNR情報取得API応答.data.travelerSupports.<当該搭乗者情報.id>.serviceStatus = ""cancelled""の場合、承れなかった旨。
      statusLabel = 'label.notApplied';
    } else if (serviceStatus === ServiceStatusTraveler.ServiceStatusTravelerEnum.Requesting) {
      // 3. PNR情報取得API応答.data.travelerSupports.<当該搭乗者情報.id>.serviceStatus = = ""requesting""の場合、リクエスト中の旨。
      statusLabel = 'label.applying';
    } else if (
      (this.pnr?.travelerSupports as any)?.[String(traveler?.id)]?.isSupportRegistered === false &&
      (!this.pnr?.air?.isContainedOtherCarrierMarketingSegment ||
        !this.pnr?.air?.isContainedOtherCarrierOperatingSegment)
    ) {
      /**
       *  4. PNR情報取得API応答.data.travelerSupports.<当該搭乗者情報.id>.isSupportRegistered=false、
       * かつ（PNR情報取得API応答.data.air.isContainedOtherCarrierMarketingSegment = false、またはPNR情報取得API応答.data.air.isContainedOtherCarrierOperatingSegment= false）
       * の場合、未登録の旨を表示する。
       */
      statusLabel = 'label.hyphen';
    }

    return isStringEmpty(statusLabel) ? '' : this._staticMsg.transform(statusLabel);
  }

  // [6.1] 障がい者種別の登録情報
  isDisplayRegistrationInformOfDisability(traveler: Traveler) {
    /**
     * 1.パーソナライズ情報aswContext.posCountryCode = 'JP' 。
     * 2.PNR情報取得API応答.data.air.tripType = 'domestic'。
     * 3.PNR情報取得API応答.data.air.isContainedDisabilityDiscountFare = true"
     */
    return (
      this._common.aswContextStoreService.aswContextData.posCountryCode === 'JP' &&
      this.air?.tripType === GetOrderResponseDataAir.TripTypeEnum.Domestic &&
      this?.air?.isContainedDisabilityDiscountFare
    );
  }

  // [6.2] 障がい者種別登録状況
  getRegistrationStatusOfDisability(traveler: Traveler): string {
    /** 1. 当該搭乗者情報.isDisabilityDiscountInfomationRegistered = trueの場合、登録済みである旨の文言を表示する。*/
    if (traveler?.isDisabilityDiscountInformationRegistered) {
      return 'label.registered';
    }
    /** 2. それ以外の場合、未登録である旨の文言を表示する。"	 */
    return 'label.hyphen';
  }

  /** [7.1] 離島カード番号の登録情報 */
  isDisplayRemoteIslandInformation(traveler: Traveler) {
    /**
     * 1.パーソナライズ情報aswContext.posCountryCode = 'JP'。
     * 2.PNR情報取得API応答.data.air.tripType = 'domestic'。
     * 3.PNR情報取得API応答.data.air.isContainedRemoteIslandFare = true"
     */
    return (
      this._common.aswContextStoreService.aswContextData.posCountryCode === 'JP' &&
      this.air?.tripType === GetOrderResponseDataAir.TripTypeEnum.Domestic &&
      this?.air?.isContainedRemoteIslandFare
    );
  }

  /**[7.2] 離島カード番号登録状況 */
  getRemoteIslandInformation(traveler: Traveler): string {
    /** 1.当該搭乗者情報.isIslandCardRegistered = trueの場合、登録済みである旨の文言を表示する。 */
    if (traveler?.isIslandCardRegistered) {
      return 'label.registered';
    }
    /** 2.それ以外の場合、未登録である旨の文言を表示する。" */
    return 'label.hyphen';
  }

  getFlightIsVisaOrResidentAlienCardRegistrationRequired() {
    /**
     * 1.1 PNR情報取得API応答.data.tasks.<セグメントID>[].isVisaOrResidentAlienCardRegistrationRequired = true要素が存在する。
     */
    /**
     * 2.1 PNR情報取得API応答.data.tasks.<セグメントID>[].isVisaOrResidentAlienCardRegistrationRequired = true要素が存在する。
     */
    return this.flights?.find(
      (flight) => this.pnr?.tasks?.[flight?.id!]?.isVisaOrResidentAlienCardRegistrationRequired === true
    );
  }

  isInfant(traveler: Traveler): boolean {
    return traveler.passengerTypeCode === PassengerType.INF;
  }

  destroy(): void {
    this._subscriptions.unsubscribe();
  }
}
